import { CherryAccountStatus } from '@/components/Timeline/types';
import { useEffect, useState } from 'react';

/**
 * Reads whether Cherry can take a charge for this patient, on demand (BAR-2604).
 *
 * `enabled` is what keeps this off the hot path: the lookup reaches Cherry
 * through `bariendo-be` and Cherry rate-limits it, so it runs when a rep picks
 * the Cherry rail and not on every timeline view. Cherry's own guidance is to
 * store the resulting `patientId` and stop re-looking-up; we don't yet, which is
 * why the budget matters.
 *
 * The callback is passed in rather than read from the timeline context so this
 * stays testable without a provider, and so the caller decides which patient is
 * being asked about.
 *
 * **A failed lookup resolves to `unknown`, never to an error state that blocks.**
 * `cherrySendBlock` treats `unknown` as permissible precisely because a lookup
 * that did not answer says nothing about whether the patient is approved.
 */
export function useCherryStatus(
  enabled: boolean,
  onCherryStatus: (() => Promise<CherryAccountStatus>) | undefined
): { status: CherryAccountStatus | undefined; loading: boolean } {
  const [status, setStatus] = useState<CherryAccountStatus | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !onCherryStatus) {
      // Clears a stale answer: a rep who picks Cherry, backs out and picks
      // Stripe must not leave a Cherry verdict behind to be re-read if they
      // switch back, since the patient may have applied in between.
      setStatus(undefined);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    onCherryStatus()
      .then((result) => {
        if (!cancelled) {
          setStatus(result);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setStatus({
            state: 'unknown',
            reason: error instanceof Error ? error.message : 'the check did not answer',
          });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, onCherryStatus]);

  return { status, loading };
}
